import { useState, useEffect } from 'preact/hooks';
import { getLeaves, approveLeave, rejectLeave, cancelLeave, createLeave, updateLeave, getLeaveTypes, getUsers } from '../lib/api';
import { useI18n } from '../i18n/I18nContext';
import { getUnitLabel } from '../lib/format-unit';

export function RequestsPage({ user, workspace }) {
  const { t, locale } = useI18n();

  // Helper to translate leave type names
  function translateLeaveType(name) {
    if (!name) return '';
    const translated = t(`leave_type_name.${name}`);
    return translated.startsWith('leave_type_name.') ? name : translated;
  }

  // Helper to format date with locale
  function formatDate(dateStr) {
    if (!dateStr) return '';
    const date = new Date(dateStr + 'T12:00:00');
    return date.toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' });
  }

  // Helper to get duration text with proper translation
  function getDurationText(val, unit) {
    if (unit === 'hours') return `${val} ${val === 1 ? t('time.hour') : t('time.hours')}`;
    if (unit === 'weeks') return `${val} ${val === 1 ? t('time.week') : t('time.weeks')}`;
    if (unit === 'months') return `${val} ${val === 1 ? t('time.month') : t('time.months')}`;
    return `${val} ${val === 1 ? t('time.day') : t('time.days')}`;
  }

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [employeeFilter, setEmployeeFilter] = useState('');
  const [allUsers, setAllUsers] = useState([]);
  const [showRejectModal, setShowRejectModal] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(null);
  const [editForm, setEditForm] = useState({
    leave_type_id: '',
    start_date: '',
    end_date: '',
    start_time: '09:00',
    end_time: '17:00',
    reason: ''
  });
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [users, setUsers] = useState([]);
  const [requestForm, setRequestForm] = useState({
    user_id: '',
    leave_type_id: '',
    start_date: '',
    end_date: '',
    start_time: '09:00',
    end_time: '17:00',
    hours: '',
    reason: ''
  });
  const [submitting, setSubmitting] = useState(false);

  // Get the selected leave type to check if it's hours-based
  const selectedLeaveType = leaveTypes.find(lt => lt.id === requestForm.leave_type_id);
  // Edit modal: unit follows the (possibly newly selected) leave type so the
  // time fields show/hide correctly when an admin changes the type.
  const editSelectedLeaveType = leaveTypes.find(lt => lt.id === editForm.leave_type_id);
  const editLeaveUnit = editSelectedLeaveType?.unit || showEditModal?.leave_type_unit || 'days';
  const isHoursBased = selectedLeaveType?.unit === 'hours';

  // Get the selected user's hours_per_day for auto-calculation
  const selectedUser = users.find(u => u.id === requestForm.user_id);
  const hoursPerDay = selectedUser?.hours_per_day || 8;

  // Calculate working days between dates (simple client-side calculation)
  function calculateWorkingDays(startDate, endDate) {
    if (!startDate || !endDate) return 0;
    const start = new Date(startDate);
    const end = new Date(endDate);
    let count = 0;
    const current = new Date(start);
    while (current <= end) {
      const day = current.getDay();
      // Count Mon-Fri by default (server will do proper calculation with holidays)
      if (day !== 0 && day !== 6) count++;
      current.setDate(current.getDate() + 1);
    }
    return count;
  }

  // Calculate hours from time difference (preserves fractional hours like 1.5h)
  function calculateHoursFromTime(startTime, endTime) {
    if (!startTime || !endTime) return 0;
    const [startH, startM] = startTime.split(':').map(Number);
    const [endH, endM] = endTime.split(':').map(Number);
    const startMinutes = startH * 60 + startM;
    const endMinutes = endH * 60 + endM;
    const diffMinutes = endMinutes - startMinutes;
    return Math.max(0, diffMinutes / 60);
  }

  // Auto-calculate hours when dates/times change for hours-based leave
  const calculatedWorkingDays = calculateWorkingDays(requestForm.start_date, requestForm.end_date || requestForm.start_date);
  const isSingleDay = !requestForm.end_date || requestForm.start_date === requestForm.end_date;
  const calculatedHoursFromTime = calculateHoursFromTime(requestForm.start_time, requestForm.end_time);

  // For single day: use time difference; for multi-day: use working days × hours_per_day
  const calculatedHours = isSingleDay ? calculatedHoursFromTime : calculatedWorkingDays * hoursPerDay;

  useEffect(() => {
    loadRequests();
  }, [filter, employeeFilter]);

  // Load users for the employee filter on mount
  useEffect(() => {
    getUsers().then(data => setAllUsers(data || [])).catch(console.error);
  }, []);

  // Auto-update hours when dates/times change (for hours-based leave)
  useEffect(() => {
    if (isHoursBased && requestForm.start_date) {
      const singleDay = !requestForm.end_date || requestForm.start_date === requestForm.end_date;
      let hours;
      if (singleDay) {
        hours = calculateHoursFromTime(requestForm.start_time, requestForm.end_time);
      } else {
        const days = calculateWorkingDays(requestForm.start_date, requestForm.end_date);
        hours = days * hoursPerDay;
      }
      if (hours > 0 && requestForm.hours !== String(hours)) {
        setRequestForm(prev => ({ ...prev, hours: String(hours) }));
      }
    }
  }, [requestForm.start_date, requestForm.end_date, requestForm.start_time, requestForm.end_time, requestForm.user_id, isHoursBased, hoursPerDay]);

  async function openRequestModal() {
    try {
      const [typesData, usersData] = await Promise.all([
        getLeaveTypes(),
        getUsers()
      ]);
      setLeaveTypes(typesData);
      setUsers(usersData);
      setRequestForm({
        user_id: user?.user_id || '',
        leave_type_id: typesData[0]?.id || '',
        start_date: '',
        end_date: '',
        start_time: '09:00',
        end_time: '17:00',
        hours: '',
        reason: ''
      });
      setShowRequestModal(true);
    } catch (error) {
      console.error('Failed to load data:', error);
    }
  }

  async function handleSubmitRequest(e) {
    e.preventDefault();
    setSubmitting(true);
    try {
      const leaveData = {
        user_id: requestForm.user_id,
        leave_type_id: requestForm.leave_type_id,
        start_date: requestForm.start_date,
        end_date: requestForm.end_date || requestForm.start_date,
        reason: requestForm.reason
      };

      // Add hours and times if this is an hours-based leave type
      if (isHoursBased && requestForm.hours) {
        const parsedHours = parseFloat(requestForm.hours);
        if (!Number.isFinite(parsedHours) || parsedHours <= 0) {
          alert('Please enter a valid number of hours.');
          return;
        }
        leaveData.hours = parsedHours;
        leaveData.start_time = requestForm.start_time;
        leaveData.end_time = requestForm.end_time;
      }

      let res = await createLeave(leaveData);

      // Paid Vacation over balance: the server asks the employee to confirm the
      // unpaid portion before it is allowed through. Re-submit with the opt-in
      // flag once confirmed.
      if (res && res.requiresUnpaidConfirmation) {
        const ok = window.confirm(
          t('requests.unpaid_confirm', { paid: res.paid, unpaid: res.unpaid, unit: res.unit })
        );
        if (!ok) {
          setSubmitting(false);
          return;
        }
        res = await createLeave({ ...leaveData, allow_unpaid: true });
      }

      setShowRequestModal(false);
      loadRequests();
    } catch (error) {
      console.error('Failed to create leave:', error);
      alert(error.message || 'Failed to create leave request');
    } finally {
      setSubmitting(false);
    }
  }

  async function loadRequests() {
    setLoading(true);
    try {
      const params = {};
      if (filter !== 'all') params.status = filter;
      if (employeeFilter) params.user_id = employeeFilter;
      const data = await getLeaves(params);
      setRequests(data);
    } catch (error) {
      console.error('Failed to load requests:', error);
    } finally {
      setLoading(false);
    }
  }

  async function handleApprove(requestId) {
    try {
      await approveLeave(requestId);
      loadRequests();
    } catch (error) {
      console.error('Failed to approve:', error);
      alert('Failed to approve: ' + (error.message || 'Unknown error'));
    }
  }

  async function handleReject() {
    if (!showRejectModal) return;
    try {
      await rejectLeave(showRejectModal, rejectReason);
      setShowRejectModal(null);
      setRejectReason('');
      loadRequests();
    } catch (error) {
      console.error('Failed to reject:', error);
      alert('Failed to reject: ' + (error.message || 'Unknown error'));
    }
  }

  async function handleCancel(requestId) {
    if (!confirm('Are you sure you want to cancel this leave? If already approved, the balance will be restored.')) return;
    try {
      await cancelLeave(requestId);
      loadRequests();
    } catch (error) {
      console.error('Failed to cancel:', error);
      alert('Failed to cancel: ' + (error.message || 'Unknown error'));
    }
  }

  async function openEditModal(request) {
    setShowEditModal(request);
    setEditForm({
      leave_type_id: request.leave_type_id || '',
      start_date: request.start_date,
      end_date: request.end_date,
      start_time: request.start_time || '09:00',
      end_time: request.end_time || '17:00',
      reason: request.reason || ''
    });
    // Ensure the leave-type dropdown has options (the create flow loads these lazily)
    if (leaveTypes.length === 0) {
      try {
        const typesData = await getLeaveTypes();
        setLeaveTypes(typesData || []);
      } catch (err) {
        console.error('Failed to load leave types:', err);
      }
    }
  }

  async function handleEditSubmit(e) {
    e.preventDefault();
    if (!showEditModal) return;
    setEditSubmitting(true);
    try {
      await updateLeave(showEditModal.id, {
        leave_type_id: editForm.leave_type_id,
        start_date: editForm.start_date,
        end_date: editForm.end_date || editForm.start_date,
        start_time: editForm.start_time,
        end_time: editForm.end_time,
        reason: editForm.reason
      });
      setShowEditModal(null);
      loadRequests();
    } catch (error) {
      console.error('Failed to update:', error);
      alert('Failed to update: ' + (error.message || 'Unknown error'));
    } finally {
      setEditSubmitting(false);
    }
  }

  const isApprover = user?.role === 'admin' || user?.role === 'owner' || user?.role === 'approver';
  const isAdmin = user?.role === 'admin' || user?.role === 'owner';

  return (
    <div>
      <div class="bento-page-header-row">
        <div class="bento-page-header">
          <h1 class="bento-page-title">{t('requests.page_title')}</h1>
          <p class="bento-page-subtitle">{t('requests.page_subtitle')}</p>
        </div>
        <button class="btn btn-primary" onClick={openRequestModal}>
          <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
          </svg>
          {t('requests.new_request')}
        </button>
      </div>

      {/* Filters */}
      <div class="bento-card bento-filters-card">
        <div class="bento-tabs" style="border-bottom: none; padding-bottom: 0; margin-bottom: 0;">
          {['pending', 'approved', 'rejected', 'all'].map(status => (
            <button
              key={status}
              class={`bento-tab ${filter === status ? 'active' : ''}`}
              onClick={() => setFilter(status)}
            >
              {t(`requests.filter.${status}`)}
            </button>
          ))}
        </div>
        <select
          class="form-select"
          value={employeeFilter}
          onChange={(e) => setEmployeeFilter(e.target.value)}
          style="width: auto; min-width: 180px;"
        >
          <option value="">{t('requests.filter.all_employees')}</option>
          {allUsers.filter(u => u.is_active).map(u => (
            <option key={u.id} value={u.id}>{u.name}</option>
          ))}
        </select>
      </div>

      <div class="bento-card bento-table-card" style="padding: 0;">
        <div style="padding: 1.5rem;">
          {loading ? (
            <div class="loading-screen" style="min-height: 200px;">
              <div class="spinner" />
            </div>
          ) : requests.length > 0 ? (
            <div class="table-container">
              <table>
                <thead>
                  <tr>
                    <th>{t('requests.table.employee')}</th>
                    <th>{t('requests.table.type')}</th>
                    <th>{t('requests.table.dates')}</th>
                    <th>{t('requests.table.duration')}</th>
                    <th>{t('requests.table.status')}</th>
                    {isApprover && <th>{t('requests.table.reason')}</th>}
                    {isApprover && (filter === 'pending' || filter === 'approved' || filter === 'all') && <th>{t('requests.table.actions')}</th>}
                  </tr>
                </thead>
                <tbody>
                  {requests.map(request => (
                    <tr key={request.id}>
                      <td>
                        <div style="display: flex; align-items: center; gap: 0.5rem;">
                          <div class="avatar">
                            {request.avatar_url ? (
                              <img src={request.avatar_url} alt="" />
                            ) : (
                              request.user_name?.charAt(0) || '?'
                            )}
                          </div>
                          {request.user_name}
                        </div>
                      </td>
                      <td>
                        <span style="display: flex; align-items: center; gap: 0.25rem;">
                          {request.icon} {translateLeaveType(request.leave_type_name)}
                        </span>
                      </td>
                      <td>
                        {request.start_date === request.end_date
                          ? (
                            <>
                              {formatDate(request.start_date)}
                              {request.start_time && request.end_time && (
                                <span style="color: #6B7280; margin-left: 0.5rem;">
                                  {request.start_time.slice(0, 5)} - {request.end_time.slice(0, 5)}
                                </span>
                              )}
                            </>
                          )
                          : `${formatDate(request.start_date)} - ${formatDate(request.end_date)}`}
                      </td>
                      <td>
                        {getDurationText(request.duration_days, request.leave_type_unit || 'days')}
                      </td>
                      <td>
                        <span class={`badge badge-${request.status}`}>
                          {t(`status.${request.status}`)}
                        </span>
                      </td>
                      {isApprover && (
                        <td style="max-width: 200px; overflow: hidden; text-overflow: ellipsis;">
                          {request.reason || '-'}
                        </td>
                      )}
                      {isApprover && (filter === 'pending' || filter === 'approved' || filter === 'all') && (
                        <td>
                          <div style="display: flex; gap: 0.5rem;">
                            {request.status === 'pending' && (
                              <>
                                <button
                                  class="btn btn-sm btn-success"
                                  onClick={() => handleApprove(request.id)}
                                >
                                  {t('requests.action.approve')}
                                </button>
                                <button
                                  class="btn btn-sm btn-danger"
                                  onClick={() => setShowRejectModal(request.id)}
                                >
                                  {t('requests.action.reject')}
                                </button>
                              </>
                            )}
                            {isAdmin && (request.status === 'approved' || request.status === 'pending') && (
                              <button
                                class="btn btn-sm btn-outline"
                                onClick={() => openEditModal(request)}
                                title="Edit leave request"
                              >
                                Edit
                              </button>
                            )}
                            {(request.status === 'approved' || request.status === 'pending') && (
                              <button
                                class="btn btn-sm btn-secondary"
                                onClick={() => handleCancel(request.id)}
                              >
                                {t('requests.action.cancel')}
                              </button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div class="empty-state">
              <div class="empty-state-icon">📋</div>
              <h3>{t('requests.empty.title')}</h3>
              <p>
                {filter === 'pending'
                  ? t('requests.empty.pending')
                  : t('requests.empty.filtered')}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Reject Modal */}
      {showRejectModal && (
        <div class="modal-overlay" onClick={() => setShowRejectModal(null)}>
          <div class="modal" onClick={(e) => e.stopPropagation()} style={{ maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
            <div class="modal-header" style={{ flexShrink: 0 }}>
              <h3>{t('requests.modal.reject_title')}</h3>
              <button class="modal-close" onClick={() => setShowRejectModal(null)}>
                <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div class="modal-body" style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
              <div class="form-group">
                <label class="form-label">{t('requests.modal.reason_label')}</label>
                <textarea
                  class="form-input"
                  rows="4"
                  placeholder={t('requests.modal.reason_placeholder')}
                  value={rejectReason}
                  onInput={(e) => setRejectReason(e.target.value)}
                />
              </div>
            </div>
            <div class="modal-footer" style={{ flexShrink: 0, background: 'white' }}>
              <button class="btn btn-secondary" onClick={() => setShowRejectModal(null)}>
                {t('common.cancel')}
              </button>
              <button class="btn btn-danger" onClick={handleReject}>
                {t('requests.modal.reject_button')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Request Leave Modal */}
      {showRequestModal && (
        <div class="modal-overlay" onClick={() => setShowRequestModal(false)}>
          <div class="modal" onClick={(e) => e.stopPropagation()} style={{ maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
            <div class="modal-header" style={{ flexShrink: 0 }}>
              <h3>{t('requests.modal.request_title')}</h3>
              <button class="modal-close" onClick={() => setShowRequestModal(false)}>
                <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <form onSubmit={handleSubmitRequest} style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
              <div class="modal-body" style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
                {(user?.role === 'admin' || user?.role === 'owner') && (
                  <div class="form-group">
                    <label class="form-label">{t('requests.modal.employee_label')}</label>
                    <select
                      class="form-input"
                      value={requestForm.user_id}
                      onChange={(e) => setRequestForm({ ...requestForm, user_id: e.target.value })}
                      required
                    >
                      {users.map(u => (
                        <option key={u.id} value={u.id}>{u.name}</option>
                      ))}
                    </select>
                  </div>
                )}

                <div class="form-group">
                  <label class="form-label">{t('requests.modal.leave_type_label')}</label>
                  <select
                    class="form-input"
                    value={requestForm.leave_type_id}
                    onChange={(e) => setRequestForm({ ...requestForm, leave_type_id: e.target.value })}
                    required
                  >
                    {leaveTypes.map(lt => (
                      <option key={lt.id} value={lt.id}>{lt.icon} {translateLeaveType(lt.name)}</option>
                    ))}
                  </select>
                  {selectedLeaveType?.minimum_duration > 0 && (
                    <small style={{ color: 'var(--color-text-muted, #6B7280)', marginTop: '0.25rem', display: 'block' }}>
                      {t('requests.modal.minimum_duration', {
                        duration: selectedLeaveType.minimum_duration,
                        unit: getUnitLabel(selectedLeaveType.unit, t)
                      })}
                    </small>
                  )}
                </div>

                {/* Date range - same for both hours and days-based leave */}
                <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 1rem;">
                  <div class="form-group">
                    <label class="form-label">{t('requests.modal.start_date_label')}</label>
                    <input
                      type="date"
                      class="form-input"
                      value={requestForm.start_date}
                      onInput={(e) => setRequestForm({ ...requestForm, start_date: e.target.value })}
                      required
                    />
                  </div>
                  <div class="form-group">
                    <label class="form-label">{t('requests.modal.end_date_label')}</label>
                    <input
                      type="date"
                      class="form-input"
                      value={requestForm.end_date}
                      onInput={(e) => setRequestForm({ ...requestForm, end_date: e.target.value })}
                      min={requestForm.start_date}
                    />
                  </div>
                </div>

                {/* Time range for hours-based leave types */}
                {isHoursBased && (
                  <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 1rem;">
                    <div class="form-group">
                      <label class="form-label">{t('requests.modal.start_time_label')}</label>
                      <input
                        type="time"
                        class="form-input"
                        value={requestForm.start_time}
                        onInput={(e) => setRequestForm({ ...requestForm, start_time: e.target.value })}
                        step="1800"
                        required
                      />
                    </div>
                    <div class="form-group">
                      <label class="form-label">{t('requests.modal.end_time_label')}</label>
                      <input
                        type="time"
                        class="form-input"
                        value={requestForm.end_time}
                        onInput={(e) => setRequestForm({ ...requestForm, end_time: e.target.value })}
                        step="1800"
                        required
                      />
                    </div>
                  </div>
                )}

                <div class="form-group">
                  <label class="form-label">{t('requests.modal.reason_optional')}</label>
                  <textarea
                    class="form-input"
                    rows="3"
                    placeholder={t('requests.modal.reason_placeholder')}
                    value={requestForm.reason}
                    onInput={(e) => setRequestForm({ ...requestForm, reason: e.target.value })}
                  />
                </div>
              </div>
              <div class="modal-footer" style={{ flexShrink: 0, background: 'white' }}>
                <button type="button" class="btn btn-secondary" onClick={() => setShowRequestModal(false)}>
                  {t('common.cancel')}
                </button>
                <button type="submit" class="btn btn-primary" disabled={submitting}>
                  {submitting ? t('requests.modal.submitting') : t('requests.modal.submit_button')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Leave Modal */}
      {showEditModal && (
        <div class="modal-overlay" onClick={() => setShowEditModal(null)}>
          <div class="modal" onClick={(e) => e.stopPropagation()} style={{ maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
            <div class="modal-header" style={{ flexShrink: 0 }}>
              <h3>Edit Leave Request</h3>
              <button class="modal-close" onClick={() => setShowEditModal(null)}>
                <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <form onSubmit={handleEditSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
              <div class="modal-body" style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
                <div style={{ marginBottom: '1rem', padding: '0.75rem', background: '#F3F4F6', borderRadius: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                    <div class="avatar" style={{ width: '24px', height: '24px', fontSize: '10px' }}>
                      {showEditModal.avatar_url ? (
                        <img src={showEditModal.avatar_url} alt="" />
                      ) : (
                        showEditModal.user_name?.charAt(0) || '?'
                      )}
                    </div>
                    <strong>{showEditModal.user_name}</strong>
                  </div>
                  <div style={{ fontSize: '0.875rem', color: '#6B7280' }}>
                    {showEditModal.icon} {translateLeaveType(showEditModal.leave_type_name)}
                    {showEditModal.status === 'approved' && (
                      <span class="badge badge-approved" style={{ marginLeft: '0.5rem' }}>Approved</span>
                    )}
                  </div>
                </div>

                {isAdmin && (
                  <div class="form-group">
                    <label class="form-label">{t('requests.modal.leave_type_label')}</label>
                    <select
                      class="form-input"
                      value={editForm.leave_type_id}
                      onChange={(e) => setEditForm({ ...editForm, leave_type_id: e.target.value })}
                    >
                      {leaveTypes.map(lt => (
                        <option key={lt.id} value={lt.id}>{lt.icon} {translateLeaveType(lt.name)}</option>
                      ))}
                    </select>
                  </div>
                )}

                <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 1rem;">
                  <div class="form-group">
                    <label class="form-label">{t('requests.modal.start_date_label')}</label>
                    <input
                      type="date"
                      class="form-input"
                      value={editForm.start_date}
                      onInput={(e) => setEditForm({ ...editForm, start_date: e.target.value })}
                      required
                    />
                  </div>
                  <div class="form-group">
                    <label class="form-label">{t('requests.modal.end_date_label')}</label>
                    <input
                      type="date"
                      class="form-input"
                      value={editForm.end_date}
                      onInput={(e) => setEditForm({ ...editForm, end_date: e.target.value })}
                      min={editForm.start_date}
                    />
                  </div>
                </div>

                {/* Time range for hours-based leave (follows the selected type) */}
                {editLeaveUnit === 'hours' && (
                  <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 1rem;">
                    <div class="form-group">
                      <label class="form-label">{t('requests.modal.start_time_label')}</label>
                      <input
                        type="time"
                        class="form-input"
                        value={editForm.start_time}
                        onInput={(e) => setEditForm({ ...editForm, start_time: e.target.value })}
                        step="1800"
                      />
                    </div>
                    <div class="form-group">
                      <label class="form-label">{t('requests.modal.end_time_label')}</label>
                      <input
                        type="time"
                        class="form-input"
                        value={editForm.end_time}
                        onInput={(e) => setEditForm({ ...editForm, end_time: e.target.value })}
                        step="1800"
                      />
                    </div>
                  </div>
                )}

                <div class="form-group">
                  <label class="form-label">{t('requests.modal.reason_optional')}</label>
                  <textarea
                    class="form-input"
                    rows="3"
                    placeholder={t('requests.modal.reason_placeholder')}
                    value={editForm.reason}
                    onInput={(e) => setEditForm({ ...editForm, reason: e.target.value })}
                  />
                </div>

                {showEditModal.status === 'approved' && (
                  <div style={{ padding: '0.75rem', background: '#FEF3C7', borderRadius: '8px', fontSize: '0.875rem', color: '#92400E' }}>
                    <strong>Note:</strong> This leave is already approved. Changing dates will automatically adjust the employee's balance.
                  </div>
                )}
              </div>
              <div class="modal-footer" style={{ flexShrink: 0, background: 'white' }}>
                <button type="button" class="btn btn-secondary" onClick={() => setShowEditModal(null)}>
                  {t('common.cancel')}
                </button>
                <button type="submit" class="btn btn-primary" disabled={editSubmitting}>
                  {editSubmitting ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
